import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore } from '../stores/gameStore';
import { MoveId } from '@elmental/shared';
import { EnergyBar } from '../components/EnergyBar';
import { MoveCard } from '../components/MoveCard';
import { Timer } from '../components/Timer';
import { OpponentInfo } from '../components/OpponentInfo';
import { RoundResult } from '../components/RoundResult';
import { submitMove, advanceRound, forfeitMatch } from '../services/gameService';
import { haptic } from '../services/telegram';
import { playSound } from '../services/audio';
import { STARTING_ENERGY, BOOST_EXTRA_ENERGY } from '@elmental/shared';
import { SwordsIcon } from '../components/icons/SwordsIcon';
import { HourglassIcon } from '../components/icons/HourglassIcon';
import { EyeIcon } from '../components/icons/EyeIcon';
import { ChartIcon } from '../components/icons/ChartIcon';

const MOVE_IDS: MoveId[] = [
  MoveId.Earth, MoveId.Fire, MoveId.Water,
  MoveId.EarthPlus, MoveId.FirePlus, MoveId.WaterPlus,
];

const PHASE_LABELS: Record<string, { text: string; color: string; bg: string }> = {
  select: { text: 'Select Move', color: '#22c55e', bg: 'rgba(34,197,94,0.15)' },
  commit: { text: 'Waiting...', color: '#eab308', bg: 'rgba(234,179,8,0.15)' },
  reveal: { text: 'Revealing!', color: '#ef4444', bg: 'rgba(239,68,68,0.15)' },
  result: { text: 'Round Result', color: '#3b82f6', bg: 'rgba(59,130,246,0.15)' },
};

export function MatchScreen() {
  const {
    opponentName,
    opponentRating,
    myEnergy,
    opponentEnergyLevel,
    myScore,
    opponentScore,
    currentRound,
    roundPhase,
    selectedMove,
    roundTimer,
    lastRoundResult,
  boostEnabled,
  } = useGameStore();

  const maxEnergy = boostEnabled ? STARTING_ENERGY + BOOST_EXTRA_ENERGY : STARTING_ENERGY;
  const [showRoundResult, setShowRoundResult] = useState(false);

  // Show result overlay when phase becomes 'result'
  useEffect(() => {
    if (roundPhase === 'result') {
      if (lastRoundResult?.result === 'win') {
        haptic.success();
        playSound('roundWin');
      } else if (lastRoundResult?.result === 'lose') {
        haptic.error();
        playSound('roundLose');
      } else {
        haptic.warning();
        playSound('roundDraw');
      }
      if (lastRoundResult?.wasOverclocked) playSound('overclock');
      setShowRoundResult(true);
      return;
    }
    setShowRoundResult(false);
  }, [roundPhase]);

  const handleMoveSelect = (moveId: MoveId) => {
    if (roundPhase !== 'select') return;
    haptic.medium();
    playSound('moveSelect');
    void submitMove(moveId);
  };

  const handleDismissResult = () => {
    setShowRoundResult(false);
    advanceRound();
  };

  const handleForfeit = () => {
    haptic.warning();
    forfeitMatch();
  };

  const phaseInfo = PHASE_LABELS[roundPhase] ?? PHASE_LABELS.select;

  return (
    <div
      className="game-match-stage relative flex flex-col h-full overflow-hidden"
    >
      {/* ── TOP BAR ──────────────────────────────────── */}
      <div
        className="mx-3 mt-3 flex items-center justify-between rounded-lg px-3 py-2"
        style={{
          background: 'oklch(18% 0.045 252 / 0.9)',
          border: '1px solid oklch(43% 0.055 252 / 0.68)',
          boxShadow: '0 12px 26px oklch(3% 0.02 252 / 0.46), 0 1px 0 oklch(100% 0 0 / 0.1) inset',
        }}
      >
        <div className="flex items-center gap-2">
          <span className="text-xs text-text-secondary font-semibold tracking-widest">ROUND</span>
          <span className="text-lg font-black text-text-primary">{currentRound}</span>
        </div>

        <motion.div
          className="flex items-center gap-3 text-2xl font-black"
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 0.3, repeat: 0 }}
          key={`${myScore}-${opponentScore}`}
        >
          <span className="text-energy-high">{myScore}</span>
          <span className="text-text-muted text-lg">:</span>
          <span className="text-energy-low">{opponentScore}</span>
        </motion.div>

        <Timer
          seconds={roundTimer}
          maxSeconds={15}
          size={44}
          strokeWidth={3}
        />
      </div>

      {/* ── OPPONENT SECTION ─────────────────────────── */}
      <div className="px-4 pt-3">
        <OpponentInfo
          name={opponentName}
          rating={opponentRating}
          energyLevel={opponentEnergyLevel}
        />
      </div>

      {/* ── VS DIVIDER ───────────────────────────────── */}
      <div className="relative flex items-center justify-center py-3">
        <div
          className="absolute inset-x-4 top-1/2 -translate-y-1/2 h-px"
          style={{ background: 'linear-gradient(90deg, transparent, oklch(72% 0.04 73 / 0.48), transparent)' }}
        />
        <motion.div
          className="relative z-10 flex items-center gap-2 px-4 py-1 rounded-full text-xs font-bold"
          style={{
            background: phaseInfo.bg,
            border: `1px solid ${phaseInfo.color}40`,
            color: phaseInfo.color,
          }}
          key={roundPhase}
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
        >
          <motion.span
            className="flex items-center justify-center"
            style={{ width: 18, height: 18 }}
            animate={roundPhase === 'commit' ? { rotate: 360 } : { rotate: 0 }}
            transition={roundPhase === 'commit' ? { duration: 2, repeat: Infinity, ease: 'linear' } : {}}
          >
            {roundPhase === 'select' ? <SwordsIcon size={18} /> :
             roundPhase === 'commit' ? <HourglassIcon size={18} /> :
             roundPhase === 'reveal' ? <EyeIcon size={18} /> : <ChartIcon size={18} />}
          </motion.span>
          {phaseInfo.text}
        </motion.div>
      </div>

      {/* ── MY ENERGY BAR ────────────────────────────── */}
      <div className="px-4">
        <EnergyBar
          energy={myEnergy}
          maxEnergy={maxEnergy}
          showNumber
          label="Your Energy"
          size="lg"
        />
      </div>

      {/* ── MOVE GRID ────────────────────────────────── */}
      <div className="flex-1 flex flex-col justify-end px-4 pb-4 pt-3 gap-2">
        <AnimatePresence mode="wait">
          {roundPhase === 'select' ? (
            <motion.div
              key="select-hint"
              className="text-center text-xs text-text-secondary"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              Choose wisely: your opponent is watching your energy level
            </motion.div>
          ) : roundPhase === 'commit' ? (
            <motion.div
              key="commit-hint"
              className="text-center text-xs"
              style={{ color: '#eab308' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              Move committed: waiting for opponent...
            </motion.div>
          ) : (
            <motion.div key="empty-hint" />
          )}
        </AnimatePresence>

        <div className="flex flex-col gap-2">
          <div className="grid grid-cols-3 gap-2">
            {MOVE_IDS.slice(0, 3).map((moveId) => (
              <MoveCard
                key={moveId}
                moveId={moveId}
                energy={myEnergy}
                selected={selectedMove === moveId}
                phase={roundPhase}
                onSelect={handleMoveSelect}
              />
            ))}
          </div>

          <div className="grid grid-cols-3 gap-2">
            {MOVE_IDS.slice(3, 6).map((moveId) => (
              <MoveCard
                key={moveId}
                moveId={moveId}
                energy={myEnergy}
                selected={selectedMove === moveId}
                phase={roundPhase}
                onSelect={handleMoveSelect}
              />
            ))}
          </div>
        </div>

        <div className="flex justify-center pt-1">
          <button
            data-nav
            className="text-xs text-text-muted px-3 py-1 rounded-full"
            style={{ background: 'oklch(20% 0.06 31 / 0.78)', border: '1px solid oklch(56% 0.19 31 / 0.34)' }}
            onClick={handleForfeit}
          >
            Forfeit match
          </button>
        </div>
      </div>

      {/* ── ROUND RESULT OVERLAY ─────────────────────── */}
      <RoundResult
        result={lastRoundResult}
        visible={showRoundResult}
        onDismiss={handleDismissResult}
      />
    </div>
  );
}
